import { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { DraggableBottomSheet } from '@/components/ui/DraggableBottomSheet';
import { ArrowLeft, Shirt, Sparkles, Search, Store, ShoppingBag, Leaf, RefreshCw, ImageOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useRegionalSettings } from '@/hooks/useRegionalSettings';
import { useSavedItems } from '@/hooks/useSavedItems';
import { useHaptics } from '@/hooks/useHaptics';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { SaveButton } from '@/components/ui/SaveButton';

interface CategoryItem {
  name: string;
  store: string;
  url: string;
  image: string;
  price: number | null;
  currency: string;
  isSecondhand?: boolean;
}

interface CategoryShopSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categoryLabel: string;
  searchQuery: string;
  imageUrl?: string;
}

export function CategoryShopSheet({
  open,
  onOpenChange,
  categoryLabel,
  searchQuery,
  imageUrl,
}: CategoryShopSheetProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<CategoryItem[]>([]);
  const [savedUrls, setSavedUrls] = useState<Set<string>>(new Set());
  const [savingUrls, setSavingUrls] = useState<Set<string>>(new Set());
  const [imgErrors, setImgErrors] = useState<Set<string>>(new Set());
  const [searchProgress, setSearchProgress] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  
  const { settings: regionalSettings, formatPrice } = useRegionalSettings();
  const { createItem } = useSavedItems();
  const { trigger: triggerHaptic } = useHaptics();
  const navigate = useNavigate();

  // Fetch profile for gender fallback
  const { data: profile } = useQuery({
    queryKey: ['profile-gender'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const { data } = await supabase.from('profiles').select('gender').eq('user_id', user.id).maybeSingle();
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const SEARCH_STAGES = [
    { icon: Search, label: 'Searching...' },
    { icon: Store, label: 'Finding retailers...' },
    { icon: ShoppingBag, label: 'Loading products...' },
  ];
  const [searchStage, setSearchStage] = useState(0);

  // Search for more products when sheet opens
  useEffect(() => {
    if (open && !hasSearched && searchQuery) {
      handleSearch();
    }
  }, [open, hasSearched, searchQuery]);

  // Reset state when closed
  useEffect(() => {
    if (!open) {
      setResults([]);
      setSavedUrls(new Set());
      setSavingUrls(new Set());
      setImgErrors(new Set());
      setHasSearched(false);
      setSearchProgress(0);
    }
  }, [open]);

  // Animate progress
  useEffect(() => {
    if (isLoading) {
      setSearchProgress(0);
      setSearchStage(0);
      
      const progressInterval = setInterval(() => {
        setSearchProgress(prev => {
          if (prev >= 95) return prev;
          return Math.min(prev + Math.random() * 15 + 5, 95);
        });
      }, 400);

      const stageInterval = setInterval(() => {
        setSearchStage(prev => (prev + 1) % SEARCH_STAGES.length);
      }, 1500);

      return () => {
        clearInterval(progressInterval);
        clearInterval(stageInterval);
      };
    }
  }, [isLoading]);

  const handleSearch = async () => {
    setIsLoading(true);
    setHasSearched(true);

    try {
      const { data, error } = await supabase.functions.invoke('find-similar-products', {
        body: {
          imageUrl,
          searchTerms: searchQuery,
          productName: categoryLabel,
          countryCode: regionalSettings.countryCode,
          currency: regionalSettings.currency,
          gender: profile?.gender || null, // Fallback; server fetches persona as source of truth
          variation: false,
        },
      });

      if (error) throw error;

      if (data?.results && Array.isArray(data.results)) {
        setResults(data.results);
      }
    } catch (err) {
      console.error('Category search error:', err);
      toast.error('Could not load more products');
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickSave = async (item: CategoryItem, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (savedUrls.has(item.url) || savingUrls.has(item.url)) return;
    
    triggerHaptic('light');
    setSavingUrls(prev => new Set(prev).add(item.url));
    
    try {
      await createItem.mutateAsync({
        name: item.name,
        url: item.url,
        image_url: item.image,
        price: item.price ?? undefined,
        currency: item.currency,
        store: item.store,
        source_type: 'url',
        save_source: 'complete_the_look',
      });
      
      setSavedUrls(prev => new Set(prev).add(item.url));
      triggerHaptic('success');
    } catch (error) {
      console.error('Failed to save item:', error);
      toast.error('Could not save this item');
    } finally {
      setSavingUrls(prev => {
        const next = new Set(prev);
        next.delete(item.url);
        return next;
      });
    }
  };

  const handleTryOn = (item: CategoryItem, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    triggerHaptic('light');
    onOpenChange(false);
    navigate('/try-on', {
      state: {
        productImage: item.image,
        productName: item.name,
        productPrice: item.price,
        productCurrency: item.currency,
      }
    });
  };

  const handleImgError = (url: string) => {
    setImgErrors(prev => new Set(prev).add(url));
  };

  return (
    <DraggableBottomSheet
      open={open}
      onOpenChange={onOpenChange}
      customHeader={
        <div className="px-5 pb-4 flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 -ml-2"
            onClick={() => onOpenChange(false)}
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <Sparkles className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold">{categoryLabel}</h2>
        </div>
      }
      height="90vh"
    >
      <div className="h-full flex flex-col overflow-hidden">
        {/* Loading state */}
        <AnimatePresence>
          {isLoading && (
            <motion.div
              className="absolute inset-0 bg-background/95 backdrop-blur-md flex flex-col items-center justify-center z-20"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="relative mb-6"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  >
                    <Sparkles className="w-10 h-10 text-primary" />
                  </motion.div>
                </div>
                <motion.div
                  className="absolute inset-0 rounded-full border-2 border-primary"
                  initial={{ scale: 1, opacity: 0.5 }}
                  animate={{ scale: 1.5, opacity: 0 }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
              </motion.div>

              <div className="flex items-center gap-2 mb-4">
                {(() => {
                  const StageIcon = SEARCH_STAGES[searchStage].icon;
                  return (
                    <motion.div
                      key={searchStage}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-2"
                    >
                      <StageIcon className="w-5 h-5 text-primary" />
                      <span className="text-base font-medium text-foreground">
                        {SEARCH_STAGES[searchStage].label}
                      </span>
                    </motion.div>
                  );
                })()}
              </div>

              <div className="w-48">
                <Progress value={searchProgress} className="h-2" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Results grid */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {!isLoading && results.length === 0 && hasSearched && (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <p className="text-muted-foreground text-center">No products found</p>
              <Button onClick={handleSearch} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4 mr-2" />
                Try again
              </Button>
            </div>
          )}

          {!isLoading && results.length > 0 && (
            <motion.div
              className="space-y-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <p className="text-sm text-muted-foreground">
                {results.length} products found
              </p>
              
              <div className="grid grid-cols-2 gap-3">
                {results.map((item, index) => (
                  <motion.a
                    key={item.url}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block group rounded-xl overflow-hidden border border-border/50 bg-card"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                  >
                    {/* Image container with overlay badges */}
                    <div className="relative aspect-square overflow-hidden bg-muted">
                      {item.image && !imgErrors.has(item.url) ? (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover bg-muted"
                          onError={() => handleImgError(item.url)}
                        />
                      ) : null}
                      <div className={`w-full h-full flex items-center justify-center ${item.image && !imgErrors.has(item.url) ? 'hidden' : ''}`}>
                        <ImageOff className="w-8 h-8 text-muted-foreground/30" />
                      </div>
                      
                      {/* Action buttons - top right vertical stack */}
                      <div className="absolute top-2 right-2 flex flex-col gap-1.5">
                        <SaveButton
                          saved={savedUrls.has(item.url)}
                          onToggle={() => handleQuickSave(item, { preventDefault: () => {}, stopPropagation: () => {} } as React.MouseEvent)}
                          size="sm"
                          variant="overlay"
                          disabled={savingUrls.has(item.url)}
                        />
                        
                        {/* Try On button */}
                        {item.image && !imgErrors.has(item.url) && (
                          <button
                            onClick={(e) => handleTryOn(item, e)}
                            className="w-8 h-8 rounded-full bg-black/20 backdrop-blur-md text-white/80 border border-white/10 flex items-center justify-center active:scale-95 transition-all shadow-sm"
                          >
                            <Shirt className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      
                      {/* Secondhand badge - top left */}
                      {item.isSecondhand && (
                        <div className="absolute top-2 left-2 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-500/90 backdrop-blur-sm shadow-sm">
                          <Leaf className="w-3 h-3 text-white" />
                          <span className="text-[10px] font-medium text-white">Pre-loved</span>
                        </div>
                      )}
                      
                      {/* Price badge - bottom left */}
                      <div className="absolute bottom-2 left-2">
                        <div className="bg-background/90 backdrop-blur-sm rounded-full px-2 py-0.5 shadow-sm">
                          <span className="text-xs font-semibold text-foreground">
                            {item.price ? formatPrice(item.price, item.currency) : 'See price'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Product info below image */}
                    <div className="px-2.5 py-2">
                      <p className="text-[11px] text-muted-foreground truncate">{item.store}</p>
                      <p className="text-xs font-medium line-clamp-2 text-foreground leading-tight mt-0.5">
                        {item.name}
                      </p>
                    </div>
                  </motion.a>
                ))}
              </div>
            </motion.div>
          )}

          {/* Loading skeletons */}
          {isLoading && (
            <div className="space-y-4">
              <Skeleton className="h-4 w-32" />
              <div className="grid grid-cols-2 gap-3">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Skeleton key={i} className="aspect-[3/4] rounded-xl" />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </DraggableBottomSheet>
  );
}
